import * as Yup from "yup";

export const createRegisterSchema = (t: (key: string) => string) =>
  Yup.object().shape({
    userType: Yup.string()
      .oneOf(["individual", "company"], t("validation.invalidUserType"))
      .required(t("validation.required")),
    firstName: Yup.string().when("userType", {
      is: "individual",
      then: (schema) =>
        schema
          .min(2, t("validation.firstNameMinLength"))
          .max(50, t("validation.firstNameMaxLength"))
          .matches(/^[a-zA-ZÀ-ÿ\s'-]+$/, t("validation.invalidFirstName"))
          .required(t("validation.required")),
      otherwise: (schema) => schema.nullable(),
    }),
    lastName: Yup.string().when("userType", {
      is: "individual",
      then: (schema) =>
        schema
          .min(2, t("validation.lastNameMinLength"))
          .max(50, t("validation.lastNameMaxLength"))
          .matches(/^[a-zA-ZÀ-ÿ\s'-]+$/, t("validation.invalidLastName"))
          .required(t("validation.required")),
      otherwise: (schema) => schema.nullable(),
    }),
    email: Yup.string()
      .min(5, t("validation.emailMinLength"))
      .max(100, t("validation.emailMaxLength"))
      .email(t("validation.invalidEmail"))
      .required(t("validation.required")),
    confirmEmail: Yup.string()
      .oneOf([Yup.ref("email")], t("validation.emailsMatch"))
      .required(t("validation.required")),
    password: Yup.string()
      .min(8, t("validation.passwordMinLength"))
      .max(128, t("validation.passwordMaxLength"))
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
        t("validation.passwordComplexity"),
      )
      .required(t("validation.required")),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password")], t("validation.passwordsMatch"))
      .required(t("validation.required")),
    termsAccepted: Yup.boolean()
      .oneOf([true], t("validation.termsRequired"))
      .required(t("validation.required")),
    phone: Yup.string()
      .min(10, t("validation.phoneMinLength"))
      .max(15, t("validation.phoneMaxLength"))
      .matches(
        /^(?:\+33|0)[1-9](?:[0-9]{8})$|^(?:\+1|1)?[2-9]\d{2}[2-9]\d{2}\d{4}$|^(?:\+|00)[1-9]\d{1,14}$/,
        t("validation.invalidPhoneFormat"),
      )
      .required(t("validation.phoneRequired")),
    address: Yup.string()
      .min(5, t("validation.addressMinLength"))
      .max(100, t("validation.addressMaxLength"))
      .required(t("validation.addressRequired")),
    city: Yup.string()
      .min(2, t("validation.cityMinLength"))
      .max(30, t("validation.cityMaxLength"))
      .matches(/^[a-zA-ZÀ-ÿ\s'-]+$/, t("validation.invalidCity"))
      .required(t("validation.cityRequired")),
    postalCode: Yup.string()
      .matches(
        /^(?:[0-9]{5}|[A-Z][0-9][A-Z] [0-9][A-Z][0-9])$/,
        t("validation.invalidPostalCodeFormat"),
      )
      .required(t("validation.postalCodeRequired")),
    companyName: Yup.string().when("userType", {
      is: "company",
      then: (schema) =>
        schema
          .min(2, t("validation.companyNameMinLength"))
          .max(100, t("validation.companyNameMaxLength"))
          .matches(
            /^[a-zA-ZÀ-ÿ0-9\s'&.-]+$/,
            t("validation.invalidCompanyName"),
          )
          .required(t("validation.companyNameRequired")),
      otherwise: (schema) => schema.nullable(),
    }),
    siret: Yup.string().when("userType", {
      is: "company",
      then: (schema) =>
        schema
          .matches(/^[0-9]{14}$/, t("validation.invalidSiretFormat"))
          .required(t("validation.siretRequired")),
      otherwise: (schema) => schema.nullable(),
    }),
  });

// Export a default schema for backward compatibility
export const registerSchema = createRegisterSchema((key: string) => key);

export interface RegisterFormValues {
  userType: "individual" | "company";
  firstName: string;
  lastName: string;
  email: string;
  confirmEmail: string;
  password: string;
  confirmPassword: string;
  termsAccepted: boolean;
  phone: string;
  address: string;
  city: string;
  postalCode: string;
  companyName?: string;
  siret?: string;
}
